import QRCode from 'qrcode'
import logoBahiol from '@/assets/logo.png'

/**
 * Genera un QR con el logo de Bahiol en el centro y lo devuelve como PNG.
 *
 * Pensado para imprimir y pegar en el local, asi que sale a 1024px: un QR
 * generado al tamano en que se ve en pantalla queda borroso al imprimirlo.
 */

/** El logo tapa parte del codigo, asi que hay dos cosas que no se pueden tocar:
 *  el nivel de correccion H (recupera hasta un 30% del codigo) y este 20%. Con
 *  un logo mas grande el QR deja de leerse en telefonos con camara mediocre. */
const PROPORCION_LOGO = 0.2

function cargarImagen(ruta) {
  return new Promise((resolver, rechazar) => {
    const img = new Image()
    img.onload = () => resolver(img)
    img.onerror = () => rechazar(new Error('No se pudo cargar el logo'))
    img.src = ruta
  })
}

/** Rectangulo con esquinas redondeadas: roundRect no existe en todos los navegadores */
function rectanguloRedondeado(ctx, x, y, ancho, alto, radio) {
  ctx.beginPath()
  ctx.moveTo(x + radio, y)
  ctx.lineTo(x + ancho - radio, y)
  ctx.quadraticCurveTo(x + ancho, y, x + ancho, y + radio)
  ctx.lineTo(x + ancho, y + alto - radio)
  ctx.quadraticCurveTo(x + ancho, y + alto, x + ancho - radio, y + alto)
  ctx.lineTo(x + radio, y + alto)
  ctx.quadraticCurveTo(x, y + alto, x, y + alto - radio)
  ctx.lineTo(x, y + radio)
  ctx.quadraticCurveTo(x, y, x + radio, y)
  ctx.closePath()
}

export async function generarQrConLogo(texto, { tamano = 1024 } = {}) {
  const lienzo = document.createElement('canvas')

  await QRCode.toCanvas(lienzo, texto, {
    errorCorrectionLevel: 'H',
    width: tamano,
    margin: 2,
    color: { dark: '#0a0a0a', light: '#ffffff' }
  })

  const ctx = lienzo.getContext('2d')
  const logo = await cargarImagen(logoBahiol)

  // El logo se encaja dentro de un cuadrado sin deformarlo
  const caja = Math.round(lienzo.width * PROPORCION_LOGO)
  const escala = Math.min(caja / logo.width, caja / logo.height)
  const anchoLogo = Math.round(logo.width * escala)
  const altoLogo = Math.round(logo.height * escala)

  const relleno = Math.round(caja * 0.14)
  const anchoFondo = anchoLogo + relleno * 2
  const altoFondo = altoLogo + relleno * 2
  const xFondo = Math.round((lienzo.width - anchoFondo) / 2)
  const yFondo = Math.round((lienzo.height - altoFondo) / 2)

  // Fondo blanco detras del logo: sin el, los modulos negros del QR se mezclan
  // con el dibujo y ni se ve el logo ni se lee el codigo
  ctx.fillStyle = '#ffffff'
  rectanguloRedondeado(ctx, xFondo, yFondo, anchoFondo, altoFondo, Math.round(caja * 0.16))
  ctx.fill()

  ctx.drawImage(
    logo,
    Math.round((lienzo.width - anchoLogo) / 2),
    Math.round((lienzo.height - altoLogo) / 2),
    anchoLogo,
    altoLogo
  )

  return lienzo.toDataURL('image/png')
}

/** Convierte el nombre de la empresa en algo valido como nombre de archivo */
function nombreDeArchivo(texto) {
  const limpio = (texto || 'bahiol')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()

  return `qr-${limpio || 'bahiol'}.png`
}

export async function descargarQr(texto, nombre) {
  const png = await generarQrConLogo(texto)
  const enlace = document.createElement('a')

  enlace.href = png
  enlace.download = nombreDeArchivo(nombre)
  document.body.appendChild(enlace)
  enlace.click()
  document.body.removeChild(enlace)
}

export default { generarQrConLogo, descargarQr }
